import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { post, C, LOGO_BASE, THUMB_BASE } from "./utils";
import { ThemeCtx, Icon, NAV_ITEMS, hasAccess } from "./AppShell";
import ReactDOM from "react-dom";

// ─── Doctypes per section (narrow = fast) ─────────────────────────────────────
const openDocTab = (doc, subId) => {
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};

const SECTION_CFG = {
  gallery:        { types:"89,88,87,86,85,84,83,82,91" },
  clinicaltrials: { types:"96"  },
  productprofile: { types:"97"  },
  news:           { types:"95"  },
  reports:        { types:"92"  },
  past:           { types:"94", extra:{ eventtype:"past"     } },
  upcoming:       { types:"94", extra:{ eventtype:"upcoming" } },
  kits:           { types:"90"  },
  echo:           { types:"95"  },
  newsletter:     { types:"99"  },
  workshop:       { types:"100" },
};
const EXCLUDE = ["home","ataglance","timelines","congress"];

const buildSections = (activeSub) =>
  NAV_ITEMS
    .filter(n => !EXCLUDE.includes(n.id) && SECTION_CFG[n.id] && hasAccess(activeSub, n.key))
    .map(n => ({ id:n.id, label:n.label, icon:n.icon }));

const ORDER_KEY = (id) => `pv_dash_order_${id}`;
const VIEW_KEY  = (id) => `pv_dash_view_${id}`;

const isImage  = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName = (p="") => p.split("/").pop();
const stripHtml= (h="") => h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";
const getPrimaryProduct    = (a=[]) => a.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getCompanyName       = c => (c&&String(c.SPONSORID)!=="0")?c.SPONSORNAME||"":"";
const getCompanyLogo       = c => (c&&String(c.SPONSORID)!=="0"&&c.COMPANY_LOGO)?LOGO_BASE+c.COMPANY_LOGO:null;

// ─── Per-section loader — sequential top-to-bottom ────────────────────────────
function useSectionDocs({ user, subproject, sectionId }) {
  const [docs,    setDocs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const cfg = SECTION_CFG[sectionId]||{};
  useEffect(() => {
    if (!user||!subproject||!cfg.types) return;
    setLoading(true); setDocs([]);
    post("advance_filter", {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      doctypeid:    cfg.types,
      stateid:      2,
      first:        0,
      last:         5,
      sort:         "latest",
      ...(cfg.extra||{}),
    }, user.AUTH_CODE)
      .then(r => { if(r.STATUS==="1"&&Array.isArray(r.DATA)) setDocs(r.DATA.slice(0,5)); })
      .catch(()=>{}).finally(()=>setLoading(false));
  }, [subproject?.SUBPROJECTID, sectionId]);
  return { docs, loading };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spin({ color }) {
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",padding:"20px" }}>
      <div style={{ width:"14px",height:"14px",border:`2px solid #E5E7EB`,
        borderTopColor:color,borderRadius:"50%",animation:"_dh 0.8s linear infinite" }}/>
      <style>{"@keyframes _dh{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ─── List row (compact) ───────────────────────────────────────────────────────
function DocRow({ doc, tPrimary, grad, subId }) {
  const [hov,setHov]=useState(false);
  const _coAll = Array.isArray(doc.COMPANY)?doc.COMPANY.filter(c=>String(c.SPONSORID)!=="0"):[];
  const cos    = _coAll;
  const co     = _coAll.find(c=>c.PRIMARY===1||c.PRIMARY==="1")||_coAll[0]||null;
  const logo   = getCompanyLogo(co);
  const sponsor= getCompanyName(co);
  const product= getPrimaryProduct(Array.isArray(doc.PRODUCTS)?doc.PRODUCTS:[]);
  const chatbot= Array.isArray(doc.CHATBOT)?doc.CHATBOT:[];
  const imgs   = chatbot.filter(a=>isImage(a.FILEPATH));
  const thumb  = imgs.length>0?THUMB_BASE+fileName(imgs[0].FILEPATH):null;
  return (
    <div onClick={()=>openDocTab(doc,subId)} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ display:"flex",alignItems:"center",gap:"8px",padding:"6px 10px",
        cursor:"pointer",transition:"background 0.1s",borderRadius:"6px",
        background:hov?`${tPrimary}07`:"transparent" }}>
      {/* Accent + logo */}
      <div style={{ display:"flex",gap:"6px",alignItems:"center",flexShrink:0 }}>
        <div style={{ width:"3px",height:"28px",borderRadius:"2px",background:grad }}/>
        <div style={{ width:"26px",height:"26px",borderRadius:"5px",overflow:"hidden",
          background:C.light,border:`1px solid ${C.border}`,
          display:"flex",alignItems:"center",justifyContent:"center" }}>
          {logo?<img src={logo} alt="" onError={e=>e.target.style.display="none"}
            style={{width:"100%",height:"100%",objectFit:"contain"}}/>
          :thumb?<img src={thumb} alt="" onError={e=>e.target.style.display="none"}
            style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          :<span style={{fontSize:"9px",fontWeight:"800",color:C.muted}}>{(sponsor||"?")[0]}</span>}
        </div>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:"11px",fontWeight:"600",color:C.text,lineHeight:1.3,
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          {product&&<span style={{color:tPrimary}}>{product}: </span>}
          {doc.SUBJECT||"Untitled"}
        </div>
        <div style={{fontSize:"9px",color:C.muted,marginTop:"1px"}}>
          {sponsor&&<span>{sponsor}</span>}
          {doc.DISPLAY_TIME&&<span style={{marginLeft:"4px"}}>· {doc.DISPLAY_TIME}</span>}
        </div>
      </div>
      <div style={{display:"flex",gap:"5px",flexShrink:0,alignItems:"center"}}>
        {doc.COUNT>0&&<span style={{display:"flex",alignItems:"center",gap:"1px",fontSize:"9px",color:C.hint}}>
          <Icon name="attach" size={9} color={C.hint}/>{doc.COUNT}
        </span>}
        {doc.COMMENTCOUNT>0&&<span style={{display:"flex",alignItems:"center",gap:"1px",fontSize:"9px",color:C.hint}}>
          <Icon name="chat" size={9} color={C.hint}/>{doc.COMMENTCOUNT}
        </span>}
      </div>
    </div>
  );
}

// ─── Section box ──────────────────────────────────────────────────────────────
function SectionBox({ section, viewMode, tPrimary, grad, onNavigate, user, subproject }) {
  const { docs, loading } = useSectionDocs({ user, subproject, sectionId:section.id });

  return (
    <div style={{ background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",
      overflow:"hidden",display:"flex",flexDirection:"column",
      boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>

      {/* Header */}
      <div style={{ display:"flex",alignItems:"center",gap:"7px",
        padding:"9px 12px",borderBottom:"1px solid #F1F5F9",flexShrink:0,
        background:`linear-gradient(90deg,${tPrimary}0A,transparent)` }}>
        <div style={{ width:"3px",height:"16px",borderRadius:"2px",background:grad,flexShrink:0 }}/>
        <Icon name={section.icon} size={13} color={tPrimary}/>
        <button onClick={()=>onNavigate(section.id)}
          style={{ fontSize:"12px",fontWeight:"700",color:tPrimary,
            background:"none",border:"none",cursor:"pointer",padding:0,
            display:"flex",alignItems:"center",gap:"2px" }}>
          {section.label} <span style={{fontSize:"13px",opacity:0.6}}>›</span>
        </button>

      </div>

      {/* Body */}
      <div style={{ flex:1 }}>
        {loading && <Spin color={tPrimary}/>}
        {!loading&&docs.length===0&&(
          <div style={{padding:"20px",textAlign:"center",color:"#CBD5E1",fontSize:"11px"}}>
            No items
          </div>
        )}
        {!loading&&docs.length>0&&(
          <div style={{padding:"4px"}}>
            {docs.map(d=><DocRow key={d.DOCUMENTID} doc={d} tPrimary={tPrimary} grad={grad} subId={subproject?.SUBPROJECTID}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Drag-to-reorder settings panel ──────────────────────────────────────────
function SettingsPanel({ sections, onReorder, onClose, tPrimary }) {
  const [order, setOrder] = useState(sections.map(s=>s.id));
  const dragIdx  = useRef(null);
  const dragOver = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  const sMap = Object.fromEntries(sections.map(s=>[s.id,s]));

  const onDragStart = (i) => { dragIdx.current = i; };
  const onDragEnter = (i) => { dragOver.current = i; setOverIdx(i); };
  const onDragEnd   = () => {
    if(dragIdx.current===null||dragOver.current===null||dragIdx.current===dragOver.current){
      dragIdx.current=null; dragOver.current=null; setOverIdx(null); return;
    }
    const arr = [...order];
    const [moved] = arr.splice(dragIdx.current,1);
    arr.splice(dragOver.current,0,moved);
    setOrder(arr);
    dragIdx.current=null; dragOver.current=null; setOverIdx(null);
  };

  return ReactDOM.createPortal(
    <div style={{position:"fixed",inset:0,zIndex:9000}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.2)"}} onClick={onClose}/>
      <div style={{position:"absolute",right:0,top:0,bottom:0,width:"290px",
        background:"#fff",boxShadow:"-8px 0 32px rgba(0,0,0,0.12)",
        display:"flex",flexDirection:"column"}}>

        {/* Header */}
        <div style={{padding:"14px 16px",flexShrink:0,
          background:`linear-gradient(135deg,${tPrimary}F0,${tPrimary}99)`}}>
          <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="rgba(255,255,255,0.9)" strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="18" x2="20" y2="18"/>
            </svg>
            <span style={{fontSize:"13px",fontWeight:"800",color:"#fff",flex:1}}>Reorder Sections</span>
            <button onClick={onClose}
              style={{border:"none",background:"rgba(255,255,255,0.2)",color:"#fff",
                width:"26px",height:"26px",borderRadius:"50%",cursor:"pointer",fontSize:"15px"}}>×</button>
          </div>
          <div style={{fontSize:"10px",color:"rgba(255,255,255,0.65)",marginTop:"5px"}}>
            Drag rows to reorder your dashboard
          </div>
        </div>

        {/* Draggable list */}
        <div style={{flex:1,overflowY:"auto",padding:"10px"}}>
          {order.map((id,i)=>{
            const s=sMap[id]; if(!s) return null;
            const isOver = overIdx===i;
            return (
              <div key={id}
                draggable
                onDragStart={()=>onDragStart(i)}
                onDragEnter={()=>onDragEnter(i)}
                onDragEnd={onDragEnd}
                onDragOver={e=>e.preventDefault()}
                style={{ display:"flex",alignItems:"center",gap:"8px",
                  padding:"8px 10px",marginBottom:"4px",borderRadius:"8px",
                  background:isOver?`${tPrimary}12`:"#F8FAFC",
                  border:`1px solid ${isOver?tPrimary:"#E5E7EB"}`,
                  cursor:"grab",transition:"all 0.1s",
                  opacity:dragIdx.current===i?0.4:1,
                  transform:isOver?"scale(1.01)":"none" }}>
                {/* Drag handle dots */}
                <svg width="10" height="14" viewBox="0 0 10 14" style={{flexShrink:0}}>
                  {[2,6,10].map(y=>[2,7].map(x=>(
                    <circle key={`${x}${y}`} cx={x} cy={y} r="1.3" fill="#CBD5E1"/>
                  )))}
                </svg>
                <Icon name={s.icon} size={13} color={tPrimary}/>
                <span style={{flex:1,fontSize:"12px",fontWeight:"600",color:"#374151",
                  userSelect:"none"}}>{s.label}</span>
                <span style={{fontSize:"10px",color:"#CBD5E1",userSelect:"none"}}>⠿</span>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{padding:"10px 14px",borderTop:"1px solid #E5E7EB",
          flexShrink:0,display:"flex",gap:"8px"}}>
          <button onClick={onClose}
            style={{flex:1,padding:"7px",borderRadius:"8px",border:"1px solid #E5E7EB",
              background:"#fff",color:"#6B7280",fontSize:"12px",fontWeight:"600",cursor:"pointer"}}>
            Cancel
          </button>
          <button onClick={()=>{ onReorder(order); onClose(); }}
            style={{flex:1,padding:"7px",borderRadius:"8px",border:"none",
              background:tPrimary,color:"#fff",fontSize:"12px",fontWeight:"700",cursor:"pointer"}}>
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function DashboardHome({ user, subproject, onNavigate }) {
  const ctx      = useContext(ThemeCtx)||{};
  const theme    = ctx.theme||ctx||{};
  const tPrimary = theme.primary||C.primary;
  const grad     = theme.tileGrad||`linear-gradient(180deg,${tPrimary},${tPrimary}88)`;
  const activeSub= ctx.activeSub||subproject;

  const allSections = buildSections(activeSub);
  const [order,    setOrder]    = useState(null);
  const orderedSections = order ? order.map(id=>allSections.find(s=>s.id===id)).filter(Boolean) : allSections;
  const [viewMode, setViewMode] = useState("grid");
  const [settings, setSettings] = useState(false);

  // Load cached order + viewMode on mount/subproject switch
  useEffect(()=>{
    if(!activeSub) return;
    const sid = activeSub.SUBPROJECTID;
    const savedView  = localStorage.getItem(VIEW_KEY(sid));
    const savedOrder = localStorage.getItem(ORDER_KEY(sid));
    if(savedView) setViewMode(savedView);
    if(savedOrder){
      try {
        const ids = JSON.parse(savedOrder);
        const valid   = ids.filter(id=>allSections.find(s=>s.id===id));
        const missing = allSections.filter(s=>!valid.includes(s.id)).map(s=>s.id);
        setOrder([...valid,...missing]);
      } catch { setOrder(allSections.map(s=>s.id)); }
    } else {
      setOrder(allSections.map(s=>s.id));
    }
  },[activeSub?.SUBPROJECTID]);

  const saveOrder = (newOrder) => {
    setOrder(newOrder);
    localStorage.setItem(ORDER_KEY(activeSub.SUBPROJECTID), JSON.stringify(newOrder));
  };

  const saveView = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_KEY(activeSub.SUBPROJECTID), mode);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",
      overflow:"hidden",background:"#F0F4F8"}}>

      {/* Toolbar */}
      <div style={{flexShrink:0,padding:"10px 16px",background:"#fff",
        borderBottom:"1px solid #E5E7EB",
        display:"flex",alignItems:"center",gap:"10px"}}>
        <div>
          <div style={{fontSize:"15px",fontWeight:"800",color:"#111827"}}>Dashboard</div>
          <div style={{fontSize:"11px",color:"#9CA3AF"}}>{activeSub?.SUMMARY}</div>
        </div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:"8px"}}>
          {/* List / Grid toggle */}
          <div style={{display:"flex",background:"#F1F5F9",borderRadius:"8px",padding:"2px",gap:"2px"}}>
            {[
              { mode:"grid", path:<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></> },
              { mode:"list", path:<><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></> },
            ].map(({mode,path})=>(
              <button key={mode} onClick={()=>saveView(mode)}
                style={{width:"30px",height:"28px",borderRadius:"6px",border:"none",
                  cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                  background:viewMode===mode?tPrimary:"transparent",
                  color:viewMode===mode?"#fff":"#9CA3AF",transition:"all 0.15s"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {path}
                </svg>
              </button>
            ))}
          </div>
          {/* Sequence button */}
          <button onClick={()=>setSettings(true)}
            style={{display:"flex",alignItems:"center",gap:"5px",padding:"5px 12px",
              borderRadius:"8px",cursor:"pointer",fontSize:"12px",fontWeight:"600",
              border:`1px solid ${tPrimary}44`,background:`${tPrimary}08`,color:tPrimary}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
            Sequence
          </button>
        </div>
      </div>

      {/* Section grid — 3 cols list, 2 cols grid */}
      <div style={{flex:1,overflowY:"auto",padding:"14px 16px"}}>
        {!order ? <Spin color={tPrimary}/> : (
          <div style={{display:"grid",
            gridTemplateColumns:viewMode==="grid"?"repeat(2,1fr)":"repeat(3,1fr)",
            gap:"12px",alignItems:"start"}}>
            {(viewMode==="grid"?orderedSections.slice(0,4):orderedSections).map(s=>(
              <SectionBox key={s.id} section={s}
                user={user} subproject={activeSub}
                viewMode={viewMode}
                tPrimary={tPrimary} grad={grad} onNavigate={onNavigate}/>
            ))}
          </div>
        )}
      </div>

      {settings&&(
        <SettingsPanel sections={orderedSections}
          onReorder={saveOrder} onClose={()=>setSettings(false)}
          tPrimary={tPrimary}/>
      )}
    </div>
  );
}